import { useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import type { WorkspaceTab } from './types';

/**
 * Keeps a record workspace's open tab in the URL.
 *
 * Every workspace stored it in `useState`, so the URL never changed: a tab could not be shared,
 * could not be bookmarked, and reload or browser-back always dropped you back on the first one.
 * There was no way to send anyone a link to an employer's Agreements, which is the tab that
 * prompted this.
 *
 * <p>The pattern is `/applications/:tab`'s, which has worked on the list pages for a while
 * (`Applications/index.tsx`). It is a route param rather than `?tab=` because a record page has
 * an id segment already: `/employers/:employerId/:tab` is unambiguous, where the list pages had
 * to use a query string precisely because `/companies/:tab` would have shadowed
 * `/companies/:companyId`.
 *
 * <p>Three behaviours worth knowing:
 *
 * - **The bare path still renders**, showing `defaultId`. It is not redirected, because a
 *   `redirectsTo` entry in `e2e/routes.mjs` carries no `shell` or `wait`, so converting the bare
 *   route would silently drop its shell assertion from the smoke matrix.
 * - **An unknown slug falls back** to `defaultId` rather than rendering an empty workspace. Tab
 *   ids change; old links should degrade to the record, not to nothing.
 * - **Navigation replaces** rather than pushes. Clicking through five tabs on one record should
 *   leave one entry in history, so Back returns to the list you came from rather than walking
 *   you through the tabs in reverse.
 */
export function useWorkspaceTab(
  tabs: WorkspaceTab[],
  defaultId: string,
): [string, (id: string) => void] {
  const params = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const slug = params.tab;
  const active = tabs.some((t) => t.id === slug) ? (slug as string) : defaultId;

  const setActive = useCallback(
    (id: string) => {
      /*
       * Built from the current pathname rather than a template, so one hook serves every
       * workspace without being told its route. The bare path gains a segment; a path that
       * already carries a tab has its last segment swapped.
       *
       * Which case applies is decided by `params.tab`, not by testing whether the last segment
       * looks like a tab id. Router has already matched the route and knows the answer, and
       * guessing gets it wrong whenever a record's own id collides with a tab id: on
       * `/employers/history` the guess stripped the employer and navigated to
       * `/employers/agreements`. Email records are keyed by slugs like `js-welcome`, so a
       * collision with `content` or `when` is not hypothetical.
       */
      const segments = pathname.replace(/\/+$/, '').split('/');
      const base = slug === undefined ? segments.join('/') : segments.slice(0, -1).join('/');
      navigate(`${base}/${id}`, { replace: true });
    },
    [navigate, pathname, slug],
  );

  return [active, setActive];
}

export default useWorkspaceTab;
